import React from 'react';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { Icon } from '@components/shared/Icon';
import { Button } from '@components/shared/Button';
import { DESKTOP_SCREEN_WIDTH, useDeviceSize } from '@hooks/useDeviceSize';

interface HistoryItemProps {
  item: string;
  onRemove: (item: string) => void;
  onSelect: (selectedItem: string) => void;
}

export const HistoryItem = ({ item, onRemove, onSelect }: HistoryItemProps): React.ReactElement => {
  const deviceSize = useDeviceSize();
  const handleRemoveButton = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onRemove(item);
  };

  const handleItemClick = (): void => {
    onSelect(item);
  };

  return (
    <div css={historyItemContainerStyles} onClick={handleItemClick} data-testid="history-item">
      <div css={historyItemStyles}>
        <Icon type="emoji-frequent" color={token('color.icon.subtle')} />
        <span css={itemTextStyles}>{item}</span>
      </div>
      <div css={[removeContainerStyles, deviceSize < DESKTOP_SCREEN_WIDTH && css({ opacity: 1 })]}>
        <Button
          onClick={handleRemoveButton}
          label=""
          iconBefore={<Icon type="dropdown-cross" color={token('color.icon')} />}
        />
        {deviceSize < DESKTOP_SCREEN_WIDTH && (
          <span css={clearButtonStyles} onClick={handleRemoveButton}>
            Clear
          </span>
        )}
      </div>
    </div>
  );
};

const removeContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,

  button: {
    span: {
      margin: '0px !important',
    },
  },
});

const historyItemContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: token('space.050'),
  cursor: 'pointer',

  button: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    outline: 'none',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },

  '&:hover': {
    backgroundColor: token('color.background.accent.gray.subtlest'),
  },

  '&:hover > div:last-child': {
    opacity: 1,
  },
});

const historyItemStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const itemTextStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: `${token('color.text')}`,
});

const clearButtonStyles = css({
  color: token('color.text.subtlest'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '18px',
});
